import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, map, mergeMap, shareReplay, Observable } from 'rxjs';
import { RawStepType } from '../../interfaces/step-type.interface';
import { API_BASE, STEPTYPES } from '../../constants/general.constants';
import { StepType } from '../../logic/step-type';

@Injectable({
  providedIn: 'root'
})
export class StepTypeService {
  private readonly http = inject(HttpClient);

  private readonly _rawData$ = new BehaviorSubject<void>(undefined);
  public apiRequest$: Observable<StepType[]>;

  public stepTypes$: Observable<StepType[]> = this._rawData$.pipe(
    mergeMap(() => this.apiRequest$),
    shareReplay(1)
  );

  // Legacy compatibility - alias for stepTypes$
  public chunkTypes$ = this.stepTypes$;

  constructor() {
    this.apiRequest$ = this.http.get<RawStepType[]>(`${API_BASE}/${STEPTYPES}`).pipe(
      map((data: RawStepType[]) => {
        return data?.map((rawStepType: RawStepType) => {
          return new StepType(rawStepType);
        });
      })
    );
  }

  public async getStepType(stepTypeId: number): Promise<StepType | undefined> {
    const stepTypes = await firstValueFrom(this.stepTypes$);
    return stepTypes.find((stepType: StepType) => stepType.steptypeId === stepTypeId);
  }

  public async getStepTypeOrDefault(stepTypeId: number): Promise<StepType> {
    const stepTypes = await firstValueFrom(this.stepTypes$);
    const stepType = stepTypes.find((stepType: StepType) => stepType.steptypeId === stepTypeId);
    return stepType || stepTypes[0];
  }

  public async getStepTypeName(steptypeId: number): Promise<string> {
    const stepTypes = await firstValueFrom(this.stepTypes$);
    const stepType = stepTypes.find((stepType: StepType) => stepType.steptypeId === steptypeId);
    return stepType?.name || 'Unknown type';
  }

  // Legacy compatibility methods - delegate to step type methods
  public async getChunkType(chunkTypeId: number): Promise<StepType | undefined> {
    return this.getStepType(chunkTypeId);
  }

  public async getChunkTypeOrDefault(chunkTypeId: number): Promise<StepType> {
    return this.getStepTypeOrDefault(chunkTypeId);
  }

  public async getChunkTypeName(steptypeId: number): Promise<string> {
    return this.getStepTypeName(steptypeId);
  }

  updateData() {
    this._rawData$.next(undefined);
  }
}
